import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import ResourceCardList from './ResourceCardList';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_BODY = "'DM Sans', sans-serif";
const SHOW = 3;

// Exact, case-insensitive equality for .ilike() -- escapes the two LIKE wildcards so a trade
// name is never treated as a pattern.
const likeExact = v => String(v).replace(/[\\%_]/g, m => `\\${m}`);

// Shown on a rejected apprenticeship application's own card: real, open alternatives in the same
// trade -- other unions' postings, trade schools, and jobs. A type with no matches is left out,
// and the whole block renders nothing if none of the three has any. `jobVisible` is the Jobs tab's
// own schedule/commute-radius check, so a suggested job is always one "View job" can land on.
export default function ExploreOtherOptions({ apprenticeship, workerId, appliedApprenticeshipIds, jobVisible = () => true, onViewApprenticeship, onViewJob, onSeeAllJobs }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // null while loading
  const trade = (apprenticeship.trade || '').trim();
  const appliedKey = [...appliedApprenticeshipIds].sort().join(',');
  // Read through a ref: the parent passes a fresh closure each render, which shouldn't refetch.
  const jobVisibleRef = useRef(jobVisible);
  jobVisibleRef.current = jobVisible;

  useEffect(() => {
    if (!trade) { setState({ apprs: [], schools: [], jobs: [], jobTradeNames: [] }); return; }
    let cancelled = false;
    (async () => {
      const todayISO = new Date().toISOString().slice(0, 10);
      // trade_career_data.apprenticeship_job_trade_names lists every trade-name spelling used on
      // apprenticeships.trade and jobs.trade_category for that occupation ("Electrician" and
      // "Electrical", "Welder" and "Welding"...). The row(s) containing this posting's trade give
      // both the school categories and the full set of names to match jobs and postings on. A
      // trade no row lists (e.g. General Labor) is deliberately unmapped: it gets no schools, and
      // jobs and other unions' postings fall back to a literal exact-name match -- never a guess.
      const { data: careerRows } = await supabase.from('trade_career_data')
        .select('apprenticeship_job_trade_names, resource_directory_categories')
        .contains('apprenticeship_job_trade_names', [trade]);
      const names = [...new Set((careerRows || []).flatMap(r => r.apprenticeship_job_trade_names || []).filter(Boolean))];
      const categories = [...new Set((careerRows || []).flatMap(r => r.resource_directory_categories || []).filter(Boolean))];

      let apprQuery = supabase.from('apprenticeships')
        .select('id, title, union_id, union_name, trade, city, state, hourly_rate_start, hourly_rate_end, duration')
        .eq('status', 'active').is('archived_at', null)
        .neq('union_id', apprenticeship.union_id);
      apprQuery = names.length ? apprQuery.in('trade', names) : apprQuery.ilike('trade', likeExact(trade));
      let jobsQuery = supabase.from('jobs')
        .select('*')
        .eq('status', 'active').or(`expires_at.is.null,expires_at.gte.${todayISO}`);
      jobsQuery = names.length ? jobsQuery.in('trade_category', names) : jobsQuery.ilike('trade_category', likeExact(trade));
      const [apprRes, schoolsRes, jobsRes, dismissRes] = await Promise.all([
        apprQuery.order('created_at', { ascending: false }),
        categories.length
          ? supabase.from('resource_directory').select('*').eq('type', 'school').eq('is_active', true).in('category', categories).order('name')
          : { data: [] },
        jobsQuery.order('featured', { ascending: false }).order('created_at', { ascending: false }),
        supabase.from('job_dismissals').select('job_id').eq('worker_id', workerId),
      ]);
      if (cancelled) return;

      const applied = new Set(appliedKey ? appliedKey.split(',') : []);
      const dismissed = new Set((dismissRes.data || []).map(d => d.job_id));
      const jobs = (jobsRes.data || []).filter(j => !dismissed.has(j.id) && jobVisibleRef.current(j));
      setState({
        apprs: (apprRes.data || []).filter(a => !applied.has(a.id)),
        schools: schoolsRes.data || [],
        jobs,
        // For the exact-name fallback, the spellings actually stored (the match ignores case).
        jobTradeNames: names.length ? names : [...new Set(jobs.map(j => j.trade_category))],
      });
    })();
    return () => { cancelled = true; };
  }, [apprenticeship.id, apprenticeship.union_id, trade, workerId, appliedKey]);

  if (!state) return null;
  const { apprs, schools, jobs, jobTradeNames } = state;
  if (!apprs.length && !schools.length && !jobs.length) return null;

  const sectionTitle = { fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '14px 0 8px' };
  const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: NAVY, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '10px 12px' };
  const linkBtn = { background: 'none', border: `1px solid ${AMBER}`, color: AMBER, fontSize: '11px', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0, whiteSpace: 'nowrap' };
  const more = (n) => n > SHOW && <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>{t('exploreOptions.andMore', { count: n - SHOW })}</p>;
  const schoolNames = [...new Set(schools.map(r => r.name))];
  const pay = (min, max, suffix = '/hr') => min ? `$${min}${max && max !== min ? `–$${max}` : ''}${suffix}` : null;

  return (
    <div data-testid="explore-other-options" style={{ marginTop: '14px', padding: '14px', borderRadius: '10px', background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.25)' }}>
      <p style={{ fontSize: '0.92rem', fontWeight: 700, color: WHITE }}>{t('exploreOptions.title')}</p>
      <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', marginTop: '3px', lineHeight: 1.5 }}>{t('exploreOptions.subtitle', { trade })}</p>

      {apprs.length > 0 && (
        <div data-testid="explore-apprenticeships">
          <p style={sectionTitle}>{t('exploreOptions.apprenticeships', { count: apprs.length })}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {apprs.slice(0, SHOW).map(a => (
              <div key={a.id} style={row}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 600, color: WHITE, overflowWrap: 'anywhere' }}>{a.title}</p>
                  <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', overflowWrap: 'anywhere' }}>
                    {[a.union_name, [a.city, a.state].filter(Boolean).join(', '), pay(a.hourly_rate_start, a.hourly_rate_end)].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <button style={linkBtn} onClick={() => onViewApprenticeship(a.id)}>{t('exploreOptions.view')}</button>
              </div>
            ))}
          </div>
          {more(apprs.length)}
        </div>
      )}

      {schools.length > 0 && (
        <div data-testid="explore-schools">
          <p style={sectionTitle}>
            {t('exploreOptions.schools', { colleges: t('resources.collegeCount', { count: schoolNames.length }), programs: t('resources.programCount', { count: schools.length }) })}
          </p>
          {/* ResourceCardList groups a college's programs under one card, so the first SHOW
              colleges (with all their programs) are shown, and the remainder counted in colleges. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ResourceCardList resources={schools.filter(r => schoolNames.slice(0, SHOW).includes(r.name))} type="school" />
          </div>
          {schoolNames.length > SHOW && (
            <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>{t('exploreOptions.andMoreColleges', { count: schoolNames.length - SHOW })}</p>
          )}
        </div>
      )}

      {jobs.length > 0 && (
        <div data-testid="explore-jobs">
          <p style={sectionTitle}>{t('exploreOptions.jobs', { count: jobs.length })}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {jobs.slice(0, SHOW).map(j => (
              <div key={j.id} style={row}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: '0.82rem', fontWeight: 600, color: WHITE, overflowWrap: 'anywhere' }}>{j.job_title}</p>
                  <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', overflowWrap: 'anywhere' }}>
                    {[j.company_name, [j.city, j.state].filter(Boolean).join(', '), pay(j.salary_min, j.salary_max)].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <button style={linkBtn} onClick={() => onViewJob(j.id)}>{t('exploreOptions.viewJob')}</button>
              </div>
            ))}
          </div>
          {jobs.length > SHOW && (
            <button onClick={() => onSeeAllJobs(jobTradeNames)} style={{ ...linkBtn, border: 'none', padding: '6px 0 0' }}>{t('exploreOptions.seeAllJobs', { count: jobs.length })}</button>
          )}
        </div>
      )}
    </div>
  );
}
